'use client'

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useRouter } from 'next/navigation'
import {
  authenticatedFetch,
  clearSession,
  getSessionToken,
  isNativeApp,
  UNAUTHORIZED_EVENT,
} from '@/lib/client-fetch'

interface User {
  id: string
  email: string
  name: string
  phone?: string // Optional phone number
  type: 'parent' | 'student' | 'driver' | 'admin' | 'promo_partner'
  // Additional optional properties that may exist on user objects
  zipCode?: string
  [key: string]: any // Allow additional properties
}

interface AuthContextType {
  user: User | null
  login: (user: User) => void
  logout: () => Promise<void>
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const router = useRouter()

  useEffect(() => {
    // Only access on client side
    if (typeof window === 'undefined') {
      setIsLoading(false)
      return
    }

    // Fetch current user from the server: session cookie on web,
    // bearer token on native (see lib/api.ts).
    const fetchUser = async () => {
      // Native with no stored token is simply signed out; skip the round trip.
      if (isNativeApp() && !getSessionToken()) {
        setUser(null)
        setIsLoading(false)
        return
      }
      try {
        const response = await authenticatedFetch('/api/auth/me', { method: 'GET' })

        if (response.ok) {
          const data = await response.json()
          if (data.user) {
            setUser(data.user)
          }
        } else {
          // No valid session
          setUser(null)
        }
      } catch (error) {
        console.error('Error fetching user:', error)
        setUser(null)
      } finally {
        setIsLoading(false)
      }
    }

    fetchUser()
  }, [])

  // A native request that carried a token got a 401: the session is gone.
  // lib/api.ts has already cleared the stored token; drop the user and go to login.
  useEffect(() => {
    const onUnauthorized = () => {
      setUser(null)
      router.replace('/login')
    }
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
  }, [router])

  const login = (userData: User) => {
    // Only update in-memory state, do NOT store in localStorage
    setUser(userData)
  }

  const logout = async () => {
    try {
      // Revoke the session: clears the cookie on web; on native the bearer
      // token is sent so the server can revoke that session.
      await authenticatedFetch('/api/auth/logout', { method: 'POST' })
    } catch (error) {
      console.error('Error during logout:', error)
    } finally {
      // Clear stored token/user and in-memory state regardless of API call result
      clearSession()
      setUser(null)
      router.push('/login')
    }
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
